import * as React from "react"
import {
  type ListNotificationsQueryResult,
  useListNotificationsQuery,
  useNotificationCreatedSubscription,
  type WebNotification,
  WebNotificationStatus,
} from "@digits-graphql/frontend/graphql-bearer"
import { useAccessibleLegalEntitiesById } from "src/frontend/components/Shared/Alerts/useAccessibleLegalEntitiesById"

interface Notifications {
  notificationResult?: ListNotificationsQueryResult
  notificationEntries: [string, WebNotification[]][]
  notificationCount: number
}

export function useNotifications() {
  const accessibleLegalEntities = useAccessibleLegalEntitiesById(true)
  const notificationResult = useListNotificationsQuery({
    context: {
      noBatch: true,
    },
    fetchPolicy: "cache-and-network",
  })

  useNotificationCreatedSubscription({
    onData: (subscriptionData) => {
      if (!subscriptionData.data.data?.notificationCreated.id) return

      // Refetch the list notification if we receive a new alert while this component is showing
      return notificationResult.refetch()
    },
  })

  const { notificationEntries, notificationCount } = React.useMemo(
    () => generateNotificationEntries(accessibleLegalEntities, notificationResult),
    [accessibleLegalEntities, notificationResult]
  )

  return React.useMemo((): Notifications => {
    if (notificationResult.loading || !notificationResult.data) {
      return {
        notificationResult,
        notificationEntries,
        notificationCount,
      }
    }

    return {
      notificationResult,
      notificationEntries,
      notificationCount,
    }
  }, [notificationEntries, notificationCount, notificationResult])
}

const generateNotificationEntries = (
  accessibleLegalEntities: ReturnType<typeof useAccessibleLegalEntitiesById>,
  notificationResult: ReturnType<typeof useListNotificationsQuery>
) => {
  const notificationsByLE = (() => {
    const notifications = new Map<string, WebNotification[]>()
    notificationResult?.data?.listNotifications.reduce((agg, notification) => {
      if (notification.status === WebNotificationStatus.Dismissed) return agg
      if (!accessibleLegalEntities.has(notification.legalEntityId)) return agg

      const existingNotifications = agg.get(notification.legalEntityId) || []
      existingNotifications.push(notification)

      agg.set(notification.legalEntityId, existingNotifications)

      return agg
    }, notifications)

    return notifications
  })()

  const notificationEntries = Array.from(notificationsByLE.entries())
  const notificationCount = Array.from(notificationsByLE.values()).flat().length

  return { notificationEntries, notificationCount }
}
